/** @jsxImportSource @emotion/react */
import React, { useMemo, useEffect } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { css } from "@emotion/react";
import {
    Area,
    AreaChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    TooltipContentProps,
    XAxis,
    YAxis,
} from "recharts";
import { Text } from "@phoenix/components";
import {
    ChartTooltip,
    ChartTooltipItem,
    defaultCartesianGridProps,
    defaultLegendProps,
    defaultXAxisProps,
    defaultYAxisProps,
    useBinInterval,
} from "@phoenix/components/chart";
import { useTimeBinScale } from "@phoenix/hooks/useTimeBin";
import { useUTCOffsetMinutes } from "@phoenix/hooks/useUTCOffsetMinutes";
import { ProjectMetricViewProps } from "@phoenix/pages/project/metrics/types";
import { formatFloat } from "@phoenix/utils/numberFormatUtils";
import type { LatencyByTimeSummaryQuery } from "./__generated__/LatencyByTimeSummaryQuery.graphql";

const containerCSS = css`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const chartHeaderCSS = css`
  margin-bottom: 24px;
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const bigNumberCSS = css`
  font-size: 38px;
  font-weight: 900;
  color: var(--ac-global-text-color-900);
  display: flex;
  align-items: center;
  gap: 12px;
  letter-spacing: -1.5px;
  margin-bottom: 4px;
`;

const iconWrapperCSS = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--ac-global-color-primary-15, rgba(99, 102, 241, 0.1));
  color: var(--ac-global-color-primary);
  font-size: 22px;
`;

const subtitleCSS = css`
  font-size: 13px;
  color: var(--ac-global-text-color-500);
`;

const chartContainerCSS = css`
  flex: 1;
  min-height: 0;
`;

const viewTracesLinkCSS = css`
  display: block;
  margin-top: 12px;
  font-size: 11px;
  color: var(--ac-global-color-primary);
  text-decoration: none;
  font-weight: 600;
  border-top: 1px solid var(--ac-global-color-grey-100);
  padding-top: 8px;
  &:hover {
    text-decoration: underline;
  }
`;

function CustomTooltip({
    active,
    payload,
    label,
    scale,
}: TooltipContentProps<number, string> & { scale?: string }) {
    if (active && payload && payload.length) {
        const date = new Date(label || "");
        const isDayScale = scale === "DAY";
        const timeStr = date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            ...(isDayScale ? {} : { hour: '2-digit', minute: '2-digit', hour12: true })
        });

        return (
            <ChartTooltip>
                {label && (
                    <Text weight="heavy" size="S" color="secondary">{timeStr}</Text>
                )}
                <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {payload.map((item: any, idx: number) => (
                        <ChartTooltipItem
                            key={idx}
                            color={item.color}
                            shape="circle"
                            name={item.name || ""}
                            value={`${formatFloat(item.value ?? 0)}ms`}
                        />
                    ))}
                </div>
                <a href="#" onClick={(e: React.MouseEvent) => e.preventDefault()} css={viewTracesLinkCSS}>
                    View traces for this period
                </a>
            </ChartTooltip>
        );
    }
    return null;
}

export function LatencyByTimeSummary({ projectId, timeRange, widgetId, widgetTitle, onDataExport }: ProjectMetricViewProps) {
    const defaultScale = useTimeBinScale({ timeRange });
    const utcOffsetMinutes = useUTCOffsetMinutes();

    // Machi, respect the aggregation! Week/Month/Day (5-35 days) should stay as DAY for cleaner views
    const scale = useMemo(() => {
        if (defaultScale === "MONTH" || defaultScale === "WEEK" || defaultScale === "DAY") return "DAY";
        return defaultScale;
    }, [defaultScale]);

    const data = useLazyLoadQuery<LatencyByTimeSummaryQuery>(
        graphql`
      query LatencyByTimeSummaryQuery(
        $projectId: ID!
        $timeRange: TimeRange!
        $timeBinConfig: TimeBinConfig!
      ) {
        project: node(id: $projectId) {
          ... on Project {
            traceLatencyMsPercentileTimeSeries(
              timeRange: $timeRange
              timeBinConfig: $timeBinConfig
            ) {
              data {
                timestamp
                p50
                p90
                p99
              }
            }
          }
        }
      }
    `,
        {
            projectId,
            timeRange: {
                start: timeRange.start?.toISOString(),
                end: timeRange.end?.toISOString(),
            },
            timeBinConfig: {
                scale,
                utcOffsetMinutes,
            },
        }
    );

    const chartData = useMemo(() => {
        const raw = (data.project?.traceLatencyMsPercentileTimeSeries?.data ?? []);
        const chart = raw.map((d: any) => ({
            timestamp: d.timestamp,
            p50: d.p50 || 0,
            p90: d.p90 || 0,
            p99: d.p99 || 0,
            _activity: (d.p50 || 0) + (d.p90 || 0) + (d.p99 || 0)
        }));

        // Machi, Smart Cropping Zoom! 🚀
        const firstActiveIdx = chart.findIndex((d: any) => d._activity > 0);
        const lastActiveIdx = chart.length - [...chart].reverse().findIndex((d: any) => d._activity > 0);

        const cropped = (firstActiveIdx === -1) ? chart : chart.slice(
            Math.max(0, firstActiveIdx - 3), // buffer for context
            Math.min(chart.length, lastActiveIdx + 3)
        );

        return cropped;
    }, [data.project?.traceLatencyMsPercentileTimeSeries?.data]);

    useEffect(() => {
        if (chartData.length > 0 && onDataExport && widgetId && widgetTitle) {
            onDataExport(widgetId, widgetTitle, chartData);
        }
    }, [chartData, onDataExport, widgetId, widgetTitle]);

    const latestP50 = useMemo(() => {
        if (!chartData.length) return 0;
        const valid = [...chartData].reverse().find((d: any) => d.p50 != null && d.p50 > 0);
        return valid?.p50 || 0;
    }, [chartData]);

    const formatBigNumber = (ms: number) => {
        if (ms === 0) return "--";
        if (ms < 1000) return `${ms.toFixed(0)} ms`;
        return `${(ms / 1000).toFixed(2)} s`;
    };

    const timeTickFormatter = (value: string) => {
        const date = new Date(value);
        if (scale === "DAY") {
            return date.toLocaleString('en-US', { month: 'numeric', day: 'numeric' });
        }
        return date.toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: '2-digit', hour12: true });
    };

    const interval = useBinInterval({ scale });

    return (
        <div css={containerCSS}>
            <div css={chartHeaderCSS}>
                <div css={bigNumberCSS}>
                    <div css={iconWrapperCSS}>🕒</div>
                    {formatBigNumber(latestP50)}
                </div>
            </div>

            <div css={chartContainerCSS}>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 20 }}>
                        <defs>
                            <linearGradient id="colorP50" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#1e40af" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#1e40af" stopOpacity={0.15} />
                            </linearGradient>
                            <linearGradient id="colorP90" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#047857" stopOpacity={0.7} />
                                <stop offset="95%" stopColor="#047857" stopOpacity={0.15} />
                            </linearGradient>
                            <linearGradient id="colorP99" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#b45309" stopOpacity={0.65} />
                                <stop offset="95%" stopColor="#b45309" stopOpacity={0.1} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid {...defaultCartesianGridProps} vertical={false} strokeDasharray="3 3" opacity={0.4} />
                        <XAxis
                            {...defaultXAxisProps}
                            dataKey="timestamp"
                            interval={interval}
                            tickFormatter={timeTickFormatter}
                            tick={{ fontSize: 10, fontWeight: 600, fill: "var(--ac-global-text-color-400)" }}
                            axisLine={false}
                        />
                        <YAxis
                            {...defaultYAxisProps}
                            width={50}
                            tickFormatter={(x: number) => x >= 1000 ? `${(x / 1000).toFixed(1)}s` : `${x}ms`}
                            tick={{ fontSize: 10, fontWeight: 600, fill: "var(--ac-global-text-color-400)" }}
                            axisLine={false}
                        />
                        <Tooltip content={(props: any) => <CustomTooltip {...props} scale={scale} />} cursor={{ stroke: "var(--ac-global-color-primary)", strokeWidth: 2, strokeDasharray: "4 4" }} />
                        <Area
                            type="monotone"
                            dataKey="p99"
                            name="p99 (Latency)"
                            stroke="#f59e0b"
                            strokeWidth={3}
                            fillOpacity={1}
                            fill="url(#colorP99)"
                            dot={{ r: 3, fill: "#eab308", stroke: "#fff", strokeWidth: 1.5 }}
                            activeDot={{ r: 6, stroke: "#fff", strokeWidth: 2 }}
                        />
                        <Area
                            type="monotone"
                            dataKey="p90"
                            name="p90"
                            stroke="#047857"
                            strokeWidth={2}
                            fillOpacity={1}
                            fill="url(#colorP90)"
                            dot={{ r: 3, fill: "#eab308", stroke: "#fff", strokeWidth: 1.5 }}
                            activeDot={{ r: 6, stroke: "#fff", strokeWidth: 2 }}
                        />
                        <Area
                            type="monotone"
                            dataKey="p50"
                            name="p50"
                            stroke="#1e40af"
                            strokeWidth={2}
                            fillOpacity={1}
                            fill="url(#colorP50)"
                            dot={{ r: 4, fill: "#eab308", stroke: "#fff", strokeWidth: 2 }}
                            activeDot={{ r: 7, stroke: "#fff", strokeWidth: 2.5 }}
                        />
                        <Legend
                            {...defaultLegendProps}
                            iconType="circle"
                            verticalAlign="bottom"
                            height={36}
                            wrapperStyle={{ paddingTop: '20px' }}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
